import { getRecordsLibrary, type RecordItems } from "@/services/getRecordsLibrary";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

type UseGetAllRecordingsInput = {
  totalItems: number;
  orderByColumn: string;
  isAscending: boolean;
  searchTerm: string;
  enabled: boolean;
};

export const useGetAllRecordings = ({ totalItems, orderByColumn, isAscending, searchTerm, enabled }: UseGetAllRecordingsInput) => {
  const { errorHandler } = useErrorHandler();

  const params: Record<string, string | number> = {
    pageSize: String(totalItems),
    pageNumber: 1,
  };

  if (orderByColumn) {
    params.orderByColumn = orderByColumn;
  }

  if (isAscending) {
    params.isAscending = String(isAscending);
  }

  if (searchTerm?.trim()) {
    params.search = searchTerm.trim();
  }

  const { data, isLoading, failureCount } = useQuery({
    queryKey: ["getAllRecordings", totalItems, orderByColumn, isAscending, searchTerm],
    queryFn: async () => {
      try {
        return (await getRecordsLibrary(params)) as RecordItems;
      } catch (error: unknown) {
        if (error instanceof AxiosError && failureCount === 3) {
          errorHandler(error, {
            eventName: "GET-ALL-RECORDINGS-FOR-BULK-SELECTION",
            showToast: true,
            properties: {
              endpoint: "/recording",
              reason: `Error fetching all recordings for bulk selection - ${error?.message}`,
            },
          });
        }
        throw error;
      }
    },
    enabled,
  });

  return { data, isLoadingAllPages: isLoading };
};
